import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    CalendarPlus,
    Loader2,
    AlertCircle,
    Check,
    X,
    RotateCcw,
    CheckCircle,
    Edit2,
    Info,
    ShieldAlert,
    Clock,
    CheckCheck,
    XCircle,
    FileSpreadsheet
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import LeaveForm from './LeaveForm';
import DataTable from './common/DataTable';

// === YARDIMCI FONKSİYONLAR ===

const normalizeUnitUsed = (val) => {
    if (val === null || val === undefined) return 1;
    if (typeof val === 'number') return val;

    if (typeof val === 'string') {
        const lower = val.toLowerCase();
        if (lower.includes('gun') || lower.includes('gün')) return 1;
        if (lower.includes('saat')) return 2;
        if (/^[0-9]+$/.test(val)) return parseInt(val, 10);
    }

    return 1; // default: Gün
};

const formatStatusLabel = (status) => {
    switch (status) {
        case 'OnayBekliyor':
            return 'Onay Bekliyor';
        case 'Onaylandi':
            return 'Onaylandı';
        case 'Reddedildi':
            return 'Reddedildi';
        case 'IptalEdildi':
            return 'İptal Edildi';
        default:
            return status || '-';
    }
};

// Toplam süreyi formatla: günlük / saatlik / net gün
const formatLeaveDuration = (item) => {
    if (!item) return '-';

    const unit = normalizeUnitUsed(item.unitUsed);
    const minutes = item.durationMinutes || 0;
    const sureGunRaw = item.sureGun ?? item.sureGunGun ?? item.sure ?? 0;
    const hasSureGun =
        typeof sureGunRaw === 'number' && !isNaN(sureGunRaw) && sureGunRaw > 0;
    const sureGun = hasSureGun ? Math.round(sureGunRaw * 100) / 100 : 0;

    // SAATLİK İZİN
    if (unit === 2 && minutes > 0) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        const parts = [];
        if (hours > 0) parts.push(`${hours} saat`);
        if (mins > 0) parts.push(`${mins} dk`);
        const timeText = parts.join(' ') || '0 dakika';

        if (hasSureGun) {
            const fracText = sureGun.toString().replace('.', ',');
            return `${fracText} gün (${timeText})`;
        }

        return timeText;
    }

    // GÜNLÜK İZİN (Backend'den gelen net iş günü)
    if (hasSureGun) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const hourStr = minutes > 0 ? ` (${hours} sa${mins > 0 ? ` ${mins} dk` : ''})` : '';
        return `${sureGun.toString().replace('.', ',')} gün${hourStr}`;
    }

    if (minutes > 0) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours} saat${mins > 0 ? ` ${mins} dk` : ''}`;
    }

    return '0 gün (Hesaplanmadı)';
};

const renderDetailedErrorMessage = (rawError) => {
    if (!rawError) return null;
    const text = String(rawError);
    const sebepIdx = text.indexOf('Sebep:');
    const cozumIdx = text.indexOf('Çözüm:');

    if (sebepIdx === -1 && cozumIdx === -1) {
        return <span className="text-sm font-medium">{text}</span>;
    }

    const titlePart = sebepIdx > 0 ? text.slice(0, sebepIdx).trim() : 'İşlem Gerçekleştirilemedi';
    const sebepPart =
        sebepIdx !== -1
            ? text.slice(sebepIdx + 6, cozumIdx !== -1 ? cozumIdx : text.length).trim()
            : null;
    const cozumPart = cozumIdx !== -1 ? text.slice(cozumIdx + 6).trim() : null;

    return (
        <div className="space-y-1.5 text-sm">
            <div className="font-bold text-red-900 dark:text-red-100">{titlePart}</div>
            {sebepPart && (
                <div className="text-red-800 dark:text-red-200">
                    <span className="font-bold underline mr-1">Sebep:</span>
                    {sebepPart}
                </div>
            )}
            {cozumPart && (
                <div className="bg-amber-50 dark:bg-amber-950/60 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 px-3 py-2 rounded-lg mt-1">
                    <span className="font-bold mr-1">💡 Çözüm:</span>
                    {cozumPart}
                </div>
            )}
        </div>
    );
};

// --- MODAL BİLEŞENLERİ ---

const ConfirmationModal = ({ show, title, onConfirm, onCancel, children }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70]">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-lg border border-gray-100 dark:border-gray-700">
                <h3 className="text-xl font-bold text-amber-500 mb-4">
                    {title}
                </h3>
                <div className="my-4 text-gray-700 dark:text-gray-300">
                    {children}
                </div>
                <div className="flex justify-end space-x-3 mt-5">
                    <button
                        onClick={onCancel}
                        className="px-5 py-2 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600"
                    >
                        Hayır, Vazgeç
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700"
                    >
                        Evet, Onayla
                    </button>
                </div>
            </div>
        </div>
    );
};

const ActionReasonModal = ({
    show,
    onClose,
    onSubmit,
    title,
    actionText,
    isSubmitting
}) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            setReason('');
            setError('');
        }
    }, [show]);

    if (!show) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (reason.trim().length < 5) {
            setError('Açıklama en az 5 karakter olmalıdır.');
            return;
        }
        onSubmit(reason.trim());
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70]">
            <form
                onSubmit={handleSubmit}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-lg border border-gray-100 dark:border-gray-700"
            >
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                    {title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Lütfen bu işlemi neden yaptığınıza dair bir açıklama giriniz. (Onaylı izin iptallerinde bakiye personele otomatik iade edilir ve puantaj güncellenir.)
                </p>
                <textarea
                    value={reason}
                    onChange={(e) => {
                        setReason(e.target.value);
                        setError('');
                    }}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200"
                    rows="4"
                    placeholder="Açıklamanızı buraya yazın..."
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                <div className="flex justify-end space-x-3 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="px-5 py-2 bg-gray-500 text-white rounded-lg"
                    >
                        Vazgeç
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex items-center px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg"
                    >
                        {isSubmitting && (
                            <Loader2 className="animate-spin w-5 h-5 mr-2" />
                        )}
                        {actionText}
                    </button>
                </div>
            </form>
        </div>
    );
};

const LeaveDetailModal = ({ show, onClose, item, loading }) => {
    if (!show) return null;
    if (!item) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-start overflow-y-auto z-[70] p-4 md:p-8">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-2xl w-full max-w-2xl my-auto">
                    <div className="flex items-center text-gray-700 dark:text-gray-200">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Yükleniyor...
                    </div>
                </div>
            </div>
        );
    }

    const startDateSource = item.startDateTime || item.baslangicTarihi;
    const endDateSource = item.endDateTime || item.bitisTarihi;

    const formatDate = (date) =>
        date ? new Date(date).toLocaleDateString('tr-TR') : ' - ';

    const formatDateTime = (date) =>
        date ? new Date(date).toLocaleString('tr-TR') : ' - ';

    const formatTime = (date) =>
        date
            ? new Date(date).toLocaleTimeString('tr-TR', {
                hour: '2-digit',
                minute: '2-digit'
            })
            : ' - ';

    const unit = normalizeUnitUsed(item.unitUsed);

    const DetailRow = ({ label, value }) =>
        value ? (
            <div className="py-2.5 grid grid-cols-3 gap-4 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0">
                <dt className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    {label}
                </dt>
                <dd className="text-sm text-gray-900 dark:text-gray-100 col-span-2 break-words">
                    {value}
                </dd>
            </div>
        ) : null;

    const history = Array.isArray(item.approvalHistory) ? item.approvalHistory : [];
    const formatActionType = (t) => {
        const v = (t || '').toLowerCase();
        if (v.includes('override')) return 'Onaysız Kayıt (Override)';
        if (v.includes('approve') || v.includes('onay')) return 'Onay';
        if (v.includes('reject') || v.includes('red')) return 'Red';
        if (v.includes('cancel') || v.includes('iptal')) return 'İptal';
        return t || '-';
    };

    return (
        <div className="fixed inset-0 z-[70] flex justify-center items-start overflow-y-auto p-4 md:p-8">
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl my-auto overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white leading-none">
                        İzin Talebi Detayları
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-6">
                    {loading && (
                        <div className="mb-4 flex items-center text-sm text-blue-600 dark:text-blue-400 font-medium">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Detaylar güncelleniyor...
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Talep Bilgileri</h4>
                            <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl px-4 py-2 border border-gray-100 dark:border-gray-800">
                                <dl>
                                    <DetailRow label="Personel" value={item.employeeFullName} />
                                    <DetailRow label="Departman" value={item.departmentName} />
                                    <DetailRow label="İzin Türü" value={item.izinTuruAdi} />
                                    <DetailRow
                                        label="Tarih"
                                        value={`${formatDate(startDateSource)} - ${formatDate(endDateSource)}`}
                                    />
                                    {unit === 2 && (
                                        <DetailRow
                                            label="Saat Aralığı"
                                            value={`${formatTime(startDateSource)} - ${formatTime(endDateSource)}`}
                                        />
                                    )}
                                    <DetailRow
                                        label="Net Süre"
                                        value={formatLeaveDuration(item)}
                                    />
                                    <DetailRow label="Açıklama" value={item.aciklama} />
                                    <DetailRow label="Durum" value={formatStatusLabel(item.durum)} />
                                </dl>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">İşlem Detayları</h4>
                            <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl px-4 py-2 border border-gray-100 dark:border-gray-800">
                                <dl>
                                    <DetailRow
                                        label="Akış"
                                        value={item.waitingForLabel || (item.totalSteps ? `${item.currentStep}/${item.totalSteps}` : 'Onay Akışı Yok')}
                                    />
                                    <DetailRow
                                        label="Oluşturan"
                                        value={item.olusturanKullaniciAdi ? `${item.olusturanKullaniciAdi} (${formatDateTime(item.createdDate)})` : null}
                                    />
                                    <DetailRow
                                        label="Onaylayan"
                                        value={item.onaylayanKullaniciAdi ? `${item.onaylayanKullaniciAdi} (${formatDateTime(item.onaylanmaTarihi)})` : null}
                                    />
                                    <DetailRow
                                        label="Reddeden"
                                        value={item.reddedenKullaniciAdi ? `${item.reddedenKullaniciAdi} (${formatDateTime(item.reddedilmeTarihi)})` : null}
                                    />
                                    <DetailRow label="Red Notu" value={item.reddetmeAciklamasi} />
                                    <DetailRow
                                        label="İptal Eden"
                                        value={item.iptalEdenKullaniciAdi ? `${item.iptalEdenKullaniciAdi} (${formatDateTime(item.iptalEdilmeTarihi)})` : null}
                                    />
                                    <DetailRow label="İptal Notu" value={item.iptalAciklamasi} />
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8">
                        <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">Onay ve İşlem Geçmişi</h4>
                        {history.length === 0 ? (
                            <div className="text-sm text-gray-500 italic">
                                {loading ? 'Yükleniyor...' : 'Henüz işlem kaydı yok.'}
                            </div>
                        ) : (
                            <div className="border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300">Adım</th>
                                            <th className="px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300">İşlem</th>
                                            <th className="px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300">İşlemi Yapan</th>
                                            <th className="px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300">Tarih</th>
                                            <th className="px-4 py-3 text-left font-bold text-gray-600 dark:text-gray-300">Not</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {history
                                            .slice()
                                            .sort((a, b) => new Date(a.actionDate) - new Date(b.actionDate))
                                            .map((h, idx) => (
                                                <tr key={`${h.stepOrder}-${h.actionDate}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200 font-medium">{h.stepOrder}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-[11px] font-bold text-gray-600 dark:text-gray-300 uppercase">
                                                            {formatActionType(h.actionType)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                                                        {h.actedAsDelegateForUsername
                                                            ? `${h.actorFullName || h.actorUsername} (Vekil: ${h.actedAsDelegateForFullName || h.actedAsDelegateForUsername})`
                                                            : (h.actorFullName || h.actorUsername)}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{formatDateTime(h.actionDate)}</td>
                                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 italic">{h.note || '-'}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex justify-end sticky bottom-0 border-t border-gray-100 dark:border-gray-700">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 text-sm font-semibold text-white bg-gray-600 hover:bg-gray-700 rounded-xl transition-all shadow-lg hover:shadow-gray-500/20"
                    >
                        Pencereyi Kapat
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- ANA SAYFA ---

export default function LeaveManagementPage() {
    const [izinler, setIzinler] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const { permissions } = useAuth();
    const [formError, setFormError] = useState(null);

    const [leaveTypes, setLeaveTypes] = useState([]);

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentItem, setCurrentItem] = useState(null);

    const [actionToProcess, setActionToProcess] = useState({
        type: null,
        item: null,
        text: ''
    });
    const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    // ÜST FİLTRELER & HIZLI SEKMELER
    const [quickTab, setQuickTab] = useState('all'); // 'all' | 'actionable' | 'pending' | 'approved' | 'rejected_cancelled'
    const [filters, setFilters] = useState({
        durum: 'Tumu',
        izinTuruId: 'Tumu',
        startDate: '',
        endDate: '',
        search: ''
    });

    const canView = useMemo(
        () => permissions.includes('leave_requests:view_list'),
        [permissions]
    );
    const canCreate = useMemo(
        () => permissions.includes('leave_requests:create'),
        [permissions]
    );
    const canUpdate = useMemo(
        () => permissions.includes('leave_requests:update'),
        [permissions]
    );
    const canManage = useMemo(
        () => permissions.includes('leave_requests:manage'),
        [permissions]
    );
    const canCancel = useMemo(
        () => permissions.includes('leave_requests:cancel') || permissions.includes('leave_requests:manage'),
        [permissions]
    );
    const canOverrideApproval = useMemo(
        () => permissions.includes('leave_requests:override_approval') || permissions.includes('leave_requests:manage'),
        [permissions]
    );

    const fetchIzinler = useCallback(async () => {
        if (!canView) {
            setError('İzin taleplerini görüntüleme yetkiniz yok.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [izinData, leaveTypeData] = await Promise.all([
                apiClient('izin'),
                apiClient('izinturu/dropdown')
            ]);
            setIzinler(izinData || []);
            setLeaveTypes(leaveTypeData || []);
        } catch (err) {
            setError(err.message || 'Veri yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        fetchIzinler();
    }, [fetchIzinler]);

    const stats = useMemo(() => {
        const total = izinler.length;
        const actionable = izinler.filter(i => i.durum === 'OnayBekliyor' && i.isActionableByMe).length;
        const pending = izinler.filter(i => i.durum === 'OnayBekliyor').length;
        const approved = izinler.filter(i => i.durum === 'Onaylandi').length;
        const rejectedOrCancelled = izinler.filter(i => i.durum === 'Reddedildi' || i.durum === 'IptalEdildi').length;
        return { total, actionable, pending, approved, rejectedOrCancelled };
    }, [izinler]);

    const handleOpenFormModal = (mode, item = null) => {
        const requiredPermission =
            mode === 'add' ? 'leave_requests:create' : 'leave_requests:update';
        if (!permissions.includes(requiredPermission)) {
            setError('Bu işlemi yapma yetkiniz bulunmamaktadır.');
            return;
        }
        setModalMode(mode);
        setCurrentItem(item);
        setFormError(null);
        setMessage(null);
        setIsFormModalOpen(true);
    };

    const handleOpenDetailModal = async (item) => {
        setCurrentItem(item);
        setIsDetailModalOpen(true);
        setIsDetailLoading(true);
        try {
            const detail = await apiClient(`izin/${item.id}`);
            if (detail) setCurrentItem(detail);
        } catch (err) {
            setError(err?.message || 'İzin detayı yüklenemedi.');
        } finally {
            setIsDetailLoading(false);
        }
    };

    const handleOpenReasonModal = (type, item) => {
        if (type === 'iptal') {
            if (!canCancel) {
                setError('İzinleri iptal etme yetkiniz yok.');
                return;
            }
        } else {
            if (!canManage) {
                setError('İzinleri yönetme (reddetme) yetkiniz yok.');
                return;
            }
            if (item?.durum === 'OnayBekliyor' && item?.isActionableByMe === false) {
                setError('Bu izin şu an sizin onay adımınızda değil.');
                return;
            }
        }

        setActionToProcess({ type, item });
        setIsReasonModalOpen(true);
    };

    const handleConfirmation = (action, item, text) => {
        if (!canManage) {
            setError('İzinleri yönetme (onaylama) yetkiniz yok.');
            return;
        }
        if (item?.durum === 'OnayBekliyor' && item?.isActionableByMe === false) {
            setError('Bu izin şu an sizin onay adımınızda değil.');
            return;
        }
        setActionToProcess({ action, item, text });
        setIsConfirmModalOpen(true);
    };

    const executeConfirmedAction = async () => {
        if (!canManage) {
            setError('İzinleri yönetme (onaylama) yetkiniz yok.');
            setIsConfirmModalOpen(false);
            return;
        }

        const { action, item } = actionToProcess;
        setIsConfirmModalOpen(false);
        setMessage(null);
        setError(null);
        try {
            const result = await action(item);
            setMessage(result.message);
            fetchIzinler();
        } catch (err) {
            setError(err.message || 'İşlem gerçekleştirilemedi.');
        }
    };

    const approveAction = (item) =>
        apiClient(`izin/${item.id}/onayla`, { method: 'PUT' });

    const handleSaveLeave = async (formData) => {
        const isNew = modalMode === 'add';

        const requiredPermission = isNew
            ? 'leave_requests:create'
            : 'leave_requests:update';
        if (!permissions.includes(requiredPermission)) {
            setFormError('Bu işlemi yapma yetkiniz bulunmamaktadır.');
            return;
        }

        setIsSubmitting(true);
        setFormError(null);
        setMessage(null);

        const url = isNew ? 'izin' : `izin/${currentItem.id}`;
        const method = isNew ? 'POST' : 'PUT';

        const izinTuruIdNum = Number(formData.izinTuruId);
        const selectedLeaveType = leaveTypes.find(
            (lt) => Number(lt.id) === izinTuruIdNum
        );

        let talepKullanimBirimi = formData.talepKullanimBirimi ?? null;

        if (!talepKullanimBirimi && selectedLeaveType) {
            const rawKb = selectedLeaveType.kullanimBirimi;
            const kb =
                rawKb === 1 || rawKb === '1' || String(rawKb).toLowerCase() === 'gun'
                    ? 1
                    : rawKb === 2 || rawKb === '2' || String(rawKb).toLowerCase() === 'saat'
                        ? 2
                        : Number(rawKb);
            if (kb === 1 || kb === 2) {
                talepKullanimBirimi = kb;
            }
        }

        const payload = {
            employeeId: Number(formData.employeeId),
            izinTuruId: izinTuruIdNum,
            baslangicTarihi: formData.baslangicTarihi,
            bitisTarihi: formData.bitisTarihi,
            aciklama: formData.aciklama || null,
            talepKullanimBirimi: talepKullanimBirimi,
            delegateUserIds: formData.delegateUserIds || null,
            ...(isNew && canOverrideApproval && formData?.onaysizKaydet
                ? {
                    onaysizKaydet: true,
                    onaysizKaydetNotu: formData.onaysizKaydetNotu || null
                }
                : {})
        };

        try {
            await apiClient(url, { method, body: payload });
            setMessage(
                `İzin talebi başarıyla ${isNew ? 'oluşturuldu' : 'güncellendi'}!`
            );
            setIsFormModalOpen(false);
            fetchIzinler();
        } catch (err) {
            setFormError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleActionWithReason = async (reason) => {
        const { type } = actionToProcess;
        if (type === 'iptal') {
            if (!canCancel) {
                alert('İzinleri iptal etme yetkiniz bulunmamaktadır.');
                setIsReasonModalOpen(false);
                return;
            }
        } else {
            if (!canManage) {
                alert('İzinleri yönetme (reddetme) yetkiniz bulunmamaktadır.');
                setIsReasonModalOpen(false);
                return;
            }
        }

        const { item } = actionToProcess;
        if (!type || !item) return;

        if (type === 'reddet' && item?.durum === 'OnayBekliyor' && item?.isActionableByMe === false) {
            alert('Bu izin şu an sizin onay adımınızda değil.');
            setIsReasonModalOpen(false);
            return;
        }

        setIsSubmitting(true);
        setMessage(null);
        setError(null);

        const url = `izin/${item.id}/${type}`;
        const body = { aciklama: reason };

        try {
            const result = await apiClient(url, { method: 'PUT', body });
            setMessage(result.message);
            setIsReasonModalOpen(false);
            fetchIzinler();
        } catch (err) {
            setError(err.message || 'İşlem sırasında bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) =>
        dateString ? new Date(dateString).toLocaleDateString('tr-TR') : '-';

    const getStatusBadge = (status) => {
        const base = 'px-2.5 py-1 text-xs font-bold rounded-full inline-flex items-center gap-1';
        switch (status) {
            case 'OnayBekliyor':
                return `${base} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200`;
            case 'Onaylandi':
                return `${base} bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200`;
            case 'Reddedildi':
                return `${base} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200`;
            case 'IptalEdildi':
                return `${base} bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
            default:
                return `${base} bg-gray-300`;
        }
    };

    // TABLO SÜTUNLARI (HER ZAMAN DETAY BUTONU GÖRÜNÜR)
    const columns = useMemo(() => {
        return [
            {
                header: 'Personel',
                accessor: 'employeeFullName',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div>
                        <div className="font-semibold text-gray-900 dark:text-white">{row.employeeFullName}</div>
                        {row.departmentName && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{row.departmentName}</div>
                        )}
                    </div>
                )
            },
            {
                header: 'İzin Türü',
                accessor: 'izinTuruAdi',
                sortable: true,
                searchable: true,
                render: (row) => (
                    <div>
                        <span className="font-medium text-gray-800 dark:text-gray-200">{row.izinTuruAdi}</span>
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {normalizeUnitUsed(row.unitUsed) === 2 ? 'Saatlik' : 'Günlük'}
                        </span>
                    </div>
                )
            },
            {
                header: 'Tarih / Saat Aralığı',
                accessor: 'baslangicTarihi',
                sortable: true,
                render: (row) => {
                    const isHourly = normalizeUnitUsed(row.unitUsed) === 2;
                    if (isHourly && row.startDateTime && row.endDateTime) {
                        const sTime = new Date(row.startDateTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                        const eTime = new Date(row.endDateTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                        return (
                            <div>
                                <div>{formatDate(row.baslangicTarihi)}</div>
                                <div className="text-xs text-blue-600 dark:text-blue-400 font-mono">{sTime} - {eTime}</div>
                            </div>
                        );
                    }
                    return `${formatDate(row.baslangicTarihi)} - ${formatDate(row.bitisTarihi)}`;
                }
            },
            {
                header: 'Net Süre',
                accessor: 'durationMinutes',
                sortable: true,
                render: (row) => (
                    <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {formatLeaveDuration(row)}
                    </span>
                )
            },
            {
                header: 'Durum',
                accessor: 'durum',
                sortable: true,
                render: (row) => (
                    <span className={getStatusBadge(row.durum)}>
                        {formatStatusLabel(row.durum)}
                    </span>
                )
            },
            {
                header: 'Onay Akışı',
                accessor: 'waitingForLabel',
                sortable: false,
                render: (row) => (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {row?.waitingForLabel || '-'}
                        </span>
                        {row.durum === 'OnayBekliyor' && row.isActionableByMe && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200">
                                Sıra Sizde
                            </span>
                        )}
                    </div>
                )
            },
            {
                header: 'İşlemler',
                headerClassName: 'text-center',
                cellClassName: 'flex justify-center',
                render: (row) => (
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={() => handleOpenDetailModal(row)}
                            title="Detaylar & Onay Geçmişi"
                            className="p-2 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50 dark:hover:bg-gray-700"
                        >
                            <Info size={18} />
                        </button>

                        {row.durum === 'OnayBekliyor' && canManage && row.isActionableByMe && (
                            <button
                                onClick={() =>
                                    handleConfirmation(
                                        approveAction,
                                        row,
                                        'onaylamak'
                                    )
                                }
                                title="Onayla"
                                className="p-2 text-emerald-600 hover:text-emerald-800 rounded-full hover:bg-emerald-50 dark:hover:bg-gray-700"
                            >
                                <Check size={18} />
                            </button>
                        )}

                        {row.durum === 'OnayBekliyor' && canManage && row.isActionableByMe && (
                            <button
                                onClick={() =>
                                    handleOpenReasonModal('reddet', row)
                                }
                                title="Reddet"
                                className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50 dark:hover:bg-gray-700"
                            >
                                <X size={18} />
                            </button>
                        )}

                        {row.durum === 'OnayBekliyor' && canUpdate && (
                            <button
                                onClick={() =>
                                    handleOpenFormModal('edit', row)
                                }
                                title="Düzenle"
                                className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50 dark:hover:bg-gray-700"
                            >
                                <Edit2 size={18} />
                            </button>
                        )}

                        {(row.durum === 'OnayBekliyor' || row.durum === 'Onaylandi') && canCancel && (
                            <button
                                onClick={() =>
                                    handleOpenReasonModal('iptal', row)
                                }
                                title="İptal Et (Bakiye İadesi)"
                                className="p-2 text-gray-500 hover:text-red-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <RotateCcw size={18} />
                            </button>
                        )}
                    </div>
                )
            }
        ];
    }, [canUpdate, canManage, canCancel]);

    // FİLTRELENMİŞ LİSTE
    const filteredIzinler = useMemo(() => {
        return izinler
            .filter((i) => {
                if (quickTab === 'actionable') return i.durum === 'OnayBekliyor' && i.isActionableByMe;
                if (quickTab === 'pending') return i.durum === 'OnayBekliyor';
                if (quickTab === 'approved') return i.durum === 'Onaylandi';
                if (quickTab === 'rejected_cancelled') return i.durum === 'Reddedildi' || i.durum === 'IptalEdildi';
                return true;
            })
            .filter((i) => {
                if (filters.durum === 'Tumu') return true;
                return i.durum === filters.durum;
            })
            .filter((i) => {
                if (filters.izinTuruId === 'Tumu') return true;
                return String(i.izinTuruId) === String(filters.izinTuruId);
            })
            .filter((i) => {
                if (!filters.startDate) return true;
                const end = i.bitisTarihi ? i.bitisTarihi.split('T')[0] : '';
                return end >= filters.startDate;
            })
            .filter((i) => {
                if (!filters.endDate) return true;
                const start = i.baslangicTarihi ? i.baslangicTarihi.split('T')[0] : '';
                return start <= filters.endDate;
            })
            .filter((i) => {
                const q = filters.search.trim().toLowerCase();
                if (!q) return true;
                return (
                    i.employeeFullName?.toLowerCase().includes(q) ||
                    i.departmentName?.toLowerCase().includes(q) ||
                    i.izinTuruAdi?.toLowerCase().includes(q) ||
                    i.aciklama?.toLowerCase().includes(q)
                );
            });
    }, [izinler, quickTab, filters]);

    const handleFilterChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    if (!canView && !loading) {
        return (
            <div className="w-full bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-700 rounded-xl p-6 flex items-center space-x-3">
                <ShieldAlert className="w-8 h-8 text-red-600 dark:text-red-300" />
                <div>
                    <h2 className="text-lg font-bold text-red-700 dark:text-red-200">
                        Yetki Hatası
                    </h2>
                    <p className="text-sm text-red-600 dark:text-red-100">
                        İzin taleplerini görüntüleme yetkiniz bulunmamaktadır.
                    </p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="animate-spin text-blue-500 w-8 h-8" />
            </div>
        );
    }

    return (
        <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700 space-y-6">
            {/* ÜST BAŞLIK */}
            <div className="flex flex-wrap justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                        İzin Talep Yönetimi
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Personel izin taleplerini vardiya ve mola kurallarına uyumlu olarak görüntüleyin, onaylayın veya iptal edin.
                    </p>
                </div>
                {canCreate && (
                    <button
                        onClick={() => handleOpenFormModal('add')}
                        className="flex items-center px-4 py-2.5 bg-emerald-600 text-white font-semibold rounded-xl shadow-sm hover:bg-emerald-700 transition-colors"
                    >
                        <CalendarPlus className="w-5 h-5 mr-2" />
                        Yeni İzin Talebi
                    </button>
                )}
            </div>

            {/* KPI KARTLARI / HIZLI SEKMELER */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <button
                    type="button"
                    onClick={() => setQuickTab('all')}
                    className={`p-3.5 rounded-xl border text-left transition-all ${
                        quickTab === 'all'
                            ? 'bg-blue-50/80 border-blue-400 dark:bg-blue-950/40 dark:border-blue-600 ring-1 ring-blue-400/30'
                            : 'bg-gray-50/70 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 hover:bg-gray-100/70'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs font-bold text-gray-500 dark:text-gray-400">
                        <span>TÜM TALEPLER</span>
                        <FileSpreadsheet size={15} className="text-blue-600" />
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{stats.total}</div>
                </button>

                <button
                    type="button"
                    onClick={() => setQuickTab('actionable')}
                    className={`p-3.5 rounded-xl border text-left transition-all ${
                        quickTab === 'actionable'
                            ? 'bg-indigo-50/90 border-indigo-500 dark:bg-indigo-950/50 dark:border-indigo-500 ring-1 ring-indigo-400/30'
                            : 'bg-gray-50/70 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 hover:bg-gray-100/70'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs font-bold text-indigo-600 dark:text-indigo-400">
                        <span>ONAYIMI BEKLEYEN</span>
                        <Clock size={15} />
                    </div>
                    <div className="text-2xl font-extrabold text-indigo-700 dark:text-indigo-300 mt-1">{stats.actionable}</div>
                </button>

                <button
                    type="button"
                    onClick={() => setQuickTab('pending')}
                    className={`p-3.5 rounded-xl border text-left transition-all ${
                        quickTab === 'pending'
                            ? 'bg-amber-50/90 border-amber-500 dark:bg-amber-950/40 dark:border-amber-600 ring-1 ring-amber-400/30'
                            : 'bg-gray-50/70 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 hover:bg-gray-100/70'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs font-bold text-amber-600 dark:text-amber-400">
                        <span>ONAY BEKLEYEN</span>
                        <Clock size={15} />
                    </div>
                    <div className="text-2xl font-extrabold text-amber-700 dark:text-amber-300 mt-1">{stats.pending}</div>
                </button>

                <button
                    type="button"
                    onClick={() => setQuickTab('approved')}
                    className={`p-3.5 rounded-xl border text-left transition-all ${
                        quickTab === 'approved'
                            ? 'bg-emerald-50/90 border-emerald-500 dark:bg-emerald-950/40 dark:border-emerald-600 ring-1 ring-emerald-400/30'
                            : 'bg-gray-50/70 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 hover:bg-gray-100/70'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs font-bold text-emerald-600 dark:text-emerald-400">
                        <span>ONAYLANAN</span>
                        <CheckCheck size={15} />
                    </div>
                    <div className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 mt-1">{stats.approved}</div>
                </button>

                <button
                    type="button"
                    onClick={() => setQuickTab('rejected_cancelled')}
                    className={`p-3.5 rounded-xl border text-left transition-all ${
                        quickTab === 'rejected_cancelled'
                            ? 'bg-rose-50/90 border-rose-500 dark:bg-rose-950/40 dark:border-rose-600 ring-1 ring-rose-400/30'
                            : 'bg-gray-50/70 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 hover:bg-gray-100/70'
                    }`}
                >
                    <div className="flex items-center justify-between text-xs font-bold text-gray-500 dark:text-gray-400">
                        <span>RED / İPTAL</span>
                        <XCircle size={15} className="text-rose-500" />
                    </div>
                    <div className="text-2xl font-extrabold text-gray-700 dark:text-gray-300 mt-1">{stats.rejectedOrCancelled}</div>
                </button>
            </div>

            {/* DETAYLI FİLTRE ÇUBUĞU */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-gray-50 dark:bg-gray-800/60 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Durum
                    </label>
                    <select
                        value={filters.durum}
                        onChange={(e) => handleFilterChange('durum', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                    >
                        <option value="Tumu">Tümü</option>
                        <option value="OnayBekliyor">Onay Bekliyor</option>
                        <option value="Onaylandi">Onaylandı</option>
                        <option value="Reddedildi">Reddedildi</option>
                        <option value="IptalEdildi">İptal Edildi</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        İzin Türü
                    </label>
                    <select
                        value={filters.izinTuruId}
                        onChange={(e) => handleFilterChange('izinTuruId', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                    >
                        <option value="Tumu">Tümü</option>
                        {leaveTypes.map((lt) => (
                            <option key={lt.id} value={lt.id}>
                                {lt.ad} ({lt.kod})
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Başlangıç Tarihi (En Erken)
                    </label>
                    <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Bitiş Tarihi (En Geç)
                    </label>
                    <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                    />
                </div>

                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Personel / Açıklama Ara
                    </label>
                    <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        placeholder="İsim, departman, açıklama..."
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100"
                    />
                </div>
            </div>

            {/* MESAJLAR */}
            {message && (
                <div className="bg-emerald-50 dark:bg-emerald-900/40 border-l-4 border-emerald-500 text-emerald-800 dark:text-emerald-200 p-3.5 rounded-lg flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 shrink-0" />
                    <span className="text-sm font-medium">{message}</span>
                </div>
            )}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/40 border-l-4 border-red-500 text-red-800 dark:text-red-200 p-4 rounded-xl flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5">
                        <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                        {renderDetailedErrorMessage(error)}
                    </div>
                    <button
                        type="button"
                        onClick={() => setError(null)}
                        className="p-1 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 text-red-600 dark:text-red-300"
                        title="Uyarıyı Kapat"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* TABLO */}
            <DataTable
                data={filteredIzinler}
                columns={columns}
                itemsPerPage={15}
                showSearch={false}
            />

            {/* FORM MODAL */}
            {isFormModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-2xl w-full max-w-2xl my-auto">
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                            {modalMode === 'add' ? 'Yeni İzin Talebi Oluştur' : 'İzin Talebini Düzenle'}
                        </h3>
                        <LeaveForm
                            initialData={currentItem}
                            onSave={handleSaveLeave}
                            onCancel={() => setIsFormModalOpen(false)}
                            isSubmitting={isSubmitting}
                            apiError={formError}
                            leaveTypes={leaveTypes}
                            canOverrideApproval={canOverrideApproval}
                        />
                    </div>
                </div>
            )}

            {/* ONAY MODALI */}
            <ConfirmationModal
                show={isConfirmModalOpen}
                title="İzin Onayı"
                onConfirm={executeConfirmedAction}
                onCancel={() => setIsConfirmModalOpen(false)}
            >
                <p>
                    <strong className="font-semibold">
                        {actionToProcess.item?.employeeFullName}
                    </strong>{' '}
                    isimli personelin izin talebini{' '}
                    <strong className="font-semibold">
                        {actionToProcess.text}
                    </strong>{' '}
                    istediğinize emin misiniz?
                </p>
            </ConfirmationModal>

            {/* RED / İPTAL MODALI */}
            <ActionReasonModal
                show={isReasonModalOpen}
                onClose={() => setIsReasonModalOpen(false)}
                onSubmit={handleActionWithReason}
                title={
                    actionToProcess.type === 'reddet'
                        ? 'İzin Talebini Reddet'
                        : 'İzin Talebini İptal Et'
                }
                actionText={
                    actionToProcess.type === 'reddet' ? 'Reddet' : 'İzni İptal Et'
                }
                isSubmitting={isSubmitting}
            />

            {/* DETAY MODALI */}
            <LeaveDetailModal
                show={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                item={currentItem}
                loading={isDetailLoading}
            />
        </div>
    );
}
